// ─────────────────────────────────────────────────────────────────────────────
// FOOD & BEVERAGE — industry pack 3 (2026-10-18).
//
// Trucks, shops, caterers and pop-ups. Everything a new Food & Beverage
// company starts with, in one file: three job types (stages with categories),
// the custom fields they carry, lead sources, cost categories, the features it
// starts with hidden, and the words its first-run screens use. Nothing from
// Window & Door: an empty price book, and none of its job types, stages,
// folders, lead sources or cost categories.
//
// FOLLOW UP needs no setting here. It is on wherever a stage's category is
// Sent (CHECKPOINT_TYPES.followUp.defaultOnCategories, stageCheckpointUtils.js),
// which in this pack is Quote Sent alone.
//
// Seeded in the browser at signup like Window & Door, so stage ids are minted
// per company (packBuilders.js). Every other id is stable and readable.
//
// Registered in utils/industryRegistry.js — read it through the registry.
// ─────────────────────────────────────────────────────────────────────────────

import { stage, buildStages } from './packBuilders';
import { buildEmptyPriceBook } from '../priceBookUtils';

export const FOOD_BEVERAGE_INDUSTRY_ID = 'foodBeverage';

// ── Custom fields ────────────────────────────────────────────────────────────
// The app's custom-field shape exactly ({ id, label, type, order } — see
// components/CustomFields.js). Each job type names the fields it carries in
// defaultCustomFields, a template; the fields seeded onto the company
// (extras.customFields.jobs) carry `jobTypeIds`, so a job shows only its own
// type's fields (utils/customFieldScope.js). The owner can change either in
// Settings → Custom Fields → Shows on.
const PRIVATE_EVENT = 'privateEvent';
const MARKET = 'market';
const POP_UP = 'popUp';

const EVENT_DATE = { id: 'fb_event_date', label: 'Event Date', type: 'date' };
const VENUE = { id: 'fb_venue', label: 'Venue', type: 'text' };
const EXPECTED_GUESTS = { id: 'fb_expected_guests', label: 'Expected Guests', type: 'number' };
const MARKET_FEE = { id: 'fb_market_fee', label: 'Market Fee', type: 'number' };

const EVENT_FIELDS = [EVENT_DATE, VENUE, EXPECTED_GUESTS];
const MARKET_FIELDS = [MARKET_FEE];

const withOrder = (fields) => fields.map((f, i) => ({ ...f, order: i }));

// The job types each group of fields shows on: the types that carry it.
const EVENT_JOB_TYPES = [PRIVATE_EVENT, POP_UP];
const MARKET_JOB_TYPES = [MARKET];

// The three shipped job types. No default folders: none were specified, so a
// job's documents start in Unfiled and the owner adds folders in Settings.
export function buildFoodBeverageJobTypes() {
  return [
    {
      id: PRIVATE_EVENT,
      name: 'Private Event',
      isDefault: true,
      basedOn: null,
      defaultStages: buildStages([
        stage('Inquiry', 'Lead'),
        stage('Quote Sent', 'Sent'),
        stage('Booked', 'Awarded'),
        stage('Event Scheduled', 'In Progress'),
        stage('Event Complete', 'Completed'),
        stage('Lost', 'Lost'),
      ]),
      defaultFolders: [],
      defaultTasks: [],
      defaultCustomFields: withOrder(EVENT_FIELDS),
      defaultPricingPresetId: null,
      order: 0,
    },
    {
      id: MARKET,
      name: 'Market',
      isDefault: true,
      basedOn: null,
      defaultStages: buildStages([
        stage('Applied', 'Lead'),
        stage('Accepted', 'Awarded'),
        stage('Scheduled', 'In Progress'),
        stage('Complete', 'Completed'),
        stage('Skipped', 'Lost'),
      ]),
      defaultFolders: [],
      defaultTasks: [],
      defaultCustomFields: withOrder(MARKET_FIELDS),
      defaultPricingPresetId: null,
      order: 1,
    },
    {
      id: POP_UP,
      name: 'Pop-up',
      isDefault: true,
      basedOn: null,
      defaultStages: buildStages([
        stage('Inquiry', 'Lead'),
        stage('Confirmed', 'Awarded'),
        stage('Scheduled', 'In Progress'),
        stage('Complete', 'Completed'),
        stage('Lost', 'Lost'),
      ]),
      defaultFolders: [],
      defaultTasks: [],
      defaultCustomFields: withOrder(EVENT_FIELDS),
      defaultPricingPresetId: null,
      order: 2,
    },
  ];
}

// Every field any type carries, once each, for extras.customFields, each
// showing on the types that carry it: Event Date, Venue and Expected Guests on
// Private Event and Pop-up, Market Fee on Market.
export function buildFoodBeverageCustomFields() {
  const scoped = [
    ...EVENT_FIELDS.map(f => [f, EVENT_JOB_TYPES]),
    ...MARKET_FIELDS.map(f => [f, MARKET_JOB_TYPES]),
  ];
  return {
    jobs: scoped.map(([f, jobTypeIds], i) => ({ ...f, order: i, jobTypeIds: [...jobTypeIds] })),
    clients: [],
    serviceTickets: [],
  };
}

export function buildFoodBeverageLeadSources() {
  return [
    { id: 'referral', name: 'Referral', isActive: true, order: 0 },
    { id: 'instagram', name: 'Instagram', isActive: true, order: 1 },
    { id: 'website', name: 'Website', isActive: true, order: 2 },
    { id: 'event_planner', name: 'Event planner', isActive: true, order: 3 },
    { id: 'corporate', name: 'Corporate', isActive: true, order: 4 },
    { id: 'property_manager', name: 'Apartment or property manager', isActive: true, order: 5 },
    { id: 'repeat_client', name: 'Repeat client', isActive: true, order: 6 },
    { id: 'walk_up', name: 'Walk-up', isActive: true, order: 7 },
    { id: 'other', name: 'Other', isActive: true, order: 8 },
  ];
}

export function buildFoodBeverageCostCategories() {
  return [
    { id: 'cups_lids', name: 'Cups & lids', isActive: true, order: 0 },
    { id: 'ingredients', name: 'Ingredients', isActive: true, order: 1 },
    { id: 'ice_supplies', name: 'Ice & supplies', isActive: true, order: 2 },
    { id: 'market_fee', name: 'Market fee', isActive: true, order: 3 },
    { id: 'labor', name: 'Labor', isActive: true, order: 4 },
    { id: 'fuel_travel', name: 'Fuel & travel', isActive: true, order: 5 },
    { id: 'other', name: 'Other', isActive: true, order: 6 },
  ];
}

export const FOOD_BEVERAGE_PACK = {
  id: FOOD_BEVERAGE_INDUSTRY_ID,
  name: 'Food & Beverage',
  // A signup answer of Food & Beverage seeds this pack (utils/signupOptions.js).
  // The public pages still call the industry "Coming soon".
  isPublic: true,
  buildDefaultJobTypes: buildFoodBeverageJobTypes,
  buildDefaultLeadSources: buildFoodBeverageLeadSources,
  buildDefaultCostCategories: buildFoodBeverageCostCategories,
  buildDefaultCustomFields: buildFoodBeverageCustomFields,
  // Sells no manufactured product. An empty book is still a SEEDED book, so
  // the read-time seeder never fills it with window-and-door manufacturers.
  buildDefaultPriceBook: buildEmptyPriceBook,
  // The window-and-door tools. Documents, Tasks, Financials, Analytics and
  // Follow Up all stay on; every one of these can be shown again from
  // Settings → Feature Visibility where it has a toggle.
  hiddenFeatures: ['pricingTab', 'initialMeasureTab', 'installTrackingTab', 'documentCruncher'],
  copy: {
    companyNamePlaceholder: 'e.g. Harbor Street Coffee',
    pipelineIntro: 'We’ve built these from how food and beverage businesses usually work.',
    jobTypeConcept: 'A kind of work you sell. Each job type carries its own set of stages, because a private event and a market day do not move through your business the same way.',
    jobNamePlaceholder: 'e.g. Johnson — Graduation Party',
    // Settings. No pricing preset in the Job Types line: the Pricing tab
    // starts hidden, and with it the preset a job type would carry.
    jobTypesIntro: 'Each job type ships its own stages, document folders, and tasks — so a private event is set up differently than a market day.',
    jobTypeNamePlaceholder: 'e.g. Wedding, Festival',
    stageNamePlaceholder: 'e.g. Inquiry, Quote Sent, Booked',
    folderNamePlaceholder: 'e.g. Contracts, Menus, Event Photos',
    permissionDescs: {
      managePricingOptions: 'Edits the dropdown lists every quote inherits. Affects all future quotes.',
      showInternalCosts: 'Costs and markup on quotes. Turn off and they see the client price only.',
    },
  },
};
